/**
 * MinistrareIA - Agent Onboarding Page
 * Página principal de criação de agente com fluxo guiado
 * 
 * UPDATED:
 * - Integração com Azure OpenAI para aprimorar respostas
 * - Animações de loading progressivas
 * - Nome do agente editável
 * - Console.log do resultado final
 * 
 * Fases: INTRO → CATEGORY → CHAT → COMPLETE
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

// Components
import { IntroCard, CategoryCards, CompleteCard } from '../../components/onboarding/OnboardingCards';
import OnboardingChatUI from '../../components/onboarding/OnboardingChatUI';

// Services
import {
  BUSINESS_CATEGORIES,
  ONBOARDING_SECTIONS,
  SECTION_ORDER,
  OBJECTIVE_QUESTIONS,
  transcribeAudio,
  buildFinalPrompt,
  saveOnboardingResult
} from '../../services/global/onboardingAgentService';

import { 
  enhanceOnboardingData, 
  buildEnhancedPrompt 
} from '../../services/agents/agentEnhancerService';

import { 
  createAgentFromOnboarding, 
  prepareOnboardingPayload 
} from '../../services/agents/agentService';

// ============================================
// PHASES
// ============================================
const PHASES = {
  INTRO: 'intro',
  CATEGORY: 'category',
  CHAT: 'chat',
  COMPLETE: 'complete'
};

// Loading stages durations
const LOADING_STAGE_DURATIONS = [1500, 2000, 1500, 1000];

// localStorage cache key
const ONBOARDING_CACHE_KEY = 'lumensales-onboarding-cache';
const CACHE_EXPIRY_DAYS = 7;

// ============================================
// MAIN COMPONENT
// ============================================
const AgentOnboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [isMobile, setIsMobile] = useState(false);

  // Detectar mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Phase state
  const [currentPhase, setCurrentPhase] = useState(PHASES.INTRO);
  
  // Category state
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  
  // Estado para sub-perguntas de objetivo
  const [objectiveFollowUp, setObjectiveFollowUp] = useState({
    active: false,
    objectiveId: null,
    questions: [],
    currentQuestionIndex: 0,
    answers: {}
  });
  
  // ESTADO ÚNICO para controlar quando o input está pronto
  const [inputReady, setInputReady] = useState(false);
  
  // Responses state
  const [responses, setResponses] = useState({});
  
  // Complete state
  const [isCreating, setIsCreating] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0);
  const [agentName, setAgentName] = useState('');
  const [enhancedResult, setEnhancedResult] = useState(null);

  // Ref to track if we've restored from cache (avoid double-restore)
  const cacheRestoredRef = useRef(false);

  // Current section
  const currentSectionKey = SECTION_ORDER[currentSectionIndex];
  const currentSection = ONBOARDING_SECTIONS[currentSectionKey];
  const totalSections = SECTION_ORDER.length;

  // ============================================
  // CACHE: Save / Restore / Clear
  // ============================================

  const saveCache = useCallback(() => {
    try {
      const cache = {
        currentSectionIndex,
        responses,
        selectedCategory: selectedCategory ? { id: selectedCategory.id, label: selectedCategory.label } : null,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(ONBOARDING_CACHE_KEY, JSON.stringify(cache));
    } catch (err) {
      console.error('[AgentOnboarding] Error saving cache:', err);
    }
  }, [currentSectionIndex, responses, selectedCategory]);

  const clearCache = useCallback(() => {
    localStorage.removeItem(ONBOARDING_CACHE_KEY);
  }, []);

  // Restore from cache on mount
  useEffect(() => {
    if (cacheRestoredRef.current) return;
    cacheRestoredRef.current = true;

    try {
      const raw = localStorage.getItem(ONBOARDING_CACHE_KEY);
      if (!raw) return;

      const cache = JSON.parse(raw);
      const savedAt = new Date(cache.savedAt);
      const daysSince = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60 * 24);

      // Expired
      if (daysSince > CACHE_EXPIRY_DAYS) {
        clearCache();
        return;
      }

      // Must have category and at least 1 section answered
      if (!cache.selectedCategory || !cache.responses || Object.keys(cache.responses).length === 0) {
        return;
      }

      console.log('[AgentOnboarding] Restoring from cache:', cache);

      // Find matching category
      const matchedCategory = BUSINESS_CATEGORIES.find(c => c.id === cache.selectedCategory.id);
      if (matchedCategory) {
        setSelectedCategory(matchedCategory);
      } else {
        setSelectedCategory(cache.selectedCategory);
      }

      setResponses(cache.responses);

      // Determine which section to resume from
      const sectionIdx = Math.min(cache.currentSectionIndex, SECTION_ORDER.length - 1);
      setCurrentSectionIndex(sectionIdx);

      // Skip directly to chat phase and show bot message for current section
      setCurrentPhase(PHASES.CHAT);
      const resumeSection = ONBOARDING_SECTIONS[SECTION_ORDER[sectionIdx]];
      setTimeout(() => {
        addBotMessage(
          `Bem-vindo de volta! Vamos continuar de onde paramos.\n\n${resumeSection?.botMessage || 'Vamos continuar!'}`,
          true
        );
      }, 500);
    } catch (err) {
      console.error('[AgentOnboarding] Error restoring cache:', err);
      clearCache();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determina a config atual (seção normal ou sub-pergunta)
  const getCurrentConfig = () => {
    if (objectiveFollowUp.active) {
      const question = objectiveFollowUp.questions[objectiveFollowUp.currentQuestionIndex];
      return {
        id: question.id,
        title: 'Detalhando o Objetivo',
        inputType: 'text_audio',
        required: false,
        placeholder: question.placeholder,
        minLength: 15,
        skipText: 'Pular esta pergunta'
      };
    }
    return currentSection;
  };

  // ============================================
  // HELPERS - Mensagens do Bot
  // ============================================

  // Adiciona mensagem do bot e controla inputReady
  const addBotMessage = useCallback((content, showInputAfter = true) => {
    setInputReady(false);
    setIsTyping(true);
    
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'bot',
        content
      }]);
      setIsTyping(false);
      
      if (showInputAfter) {
        setTimeout(() => {
          setInputReady(true);
        }, 300);
      }
    }, 800);
  }, []);

  // Adiciona mensagem do usuário (input fica escondido)
  const addUserMessage = useCallback((content) => {
    setInputReady(false);
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content
    }]);
  }, []);

  // ============================================
  // PHASE HANDLERS
  // ============================================

  const handleStart = () => {
    setCurrentPhase(PHASES.CATEGORY);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);

    // Save initial cache with category
    try {
      const cache = {
        currentSectionIndex: 0,
        responses: {},
        selectedCategory: { id: category.id, label: category.label },
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(ONBOARDING_CACHE_KEY, JSON.stringify(cache));
    } catch (e) { /* ignore */ }

    setTimeout(() => {
      setCurrentPhase(PHASES.CHAT);
      setTimeout(() => {
        addBotMessage(currentSection?.botMessage || 'Vamos começar!', true);
      }, 300);
    }, 400);
  };

  // ============================================
  // CHAT HANDLERS
  // ============================================

  const goToNextSection = useCallback(() => {
    const nextIndex = currentSectionIndex + 1;

    if (nextIndex >= SECTION_ORDER.length) {
      handleComplete();
    } else {
      setCurrentSectionIndex(nextIndex);
      const nextSection = ONBOARDING_SECTIONS[SECTION_ORDER[nextIndex]];

      // Save progress to cache
      try {
        const cache = {
          currentSectionIndex: nextIndex,
          responses,
          selectedCategory: selectedCategory ? { id: selectedCategory.id, label: selectedCategory.label } : null,
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem(ONBOARDING_CACHE_KEY, JSON.stringify(cache));
      } catch (e) { /* ignore */ }

      setTimeout(() => {
        addBotMessage(nextSection.botMessage, true);
      }, 300);
    }
  }, [currentSectionIndex, responses, selectedCategory, addBotMessage]);

  // Inicia as sub-perguntas do objetivo
  const startObjectiveFollowUp = useCallback((objectiveId) => {
    const questions = OBJECTIVE_QUESTIONS[objectiveId];
    
    if (questions && questions.length > 0) {
      setObjectiveFollowUp({
        active: true,
        objectiveId,
        questions,
        currentQuestionIndex: 0,
        answers: {}
      });
      
      // Mostra primeira sub-pergunta
      setTimeout(() => {
        addBotMessage(questions[0].question, true);
      }, 300);
    } else {
      // Sem sub-perguntas, vai para próxima seção
      goToNextSection();
    }
  }, [addBotMessage, goToNextSection]);

  // Avança para próxima sub-pergunta ou finaliza
  const advanceObjectiveFollowUp = useCallback((answer = null) => {
    const { questions, currentQuestionIndex, answers, objectiveId } = objectiveFollowUp;
    
    // Salva resposta se houver
    const newAnswers = answer 
      ? { ...answers, [questions[currentQuestionIndex].id]: answer }
      : answers;
    
    const nextIndex = currentQuestionIndex + 1;
    
    if (nextIndex >= questions.length) {
      // Finaliza sub-perguntas
      setResponses(prev => ({
        ...prev,
        objective_details: newAnswers
      }));
      
      setObjectiveFollowUp({
        active: false,
        objectiveId: null,
        questions: [],
        currentQuestionIndex: 0,
        answers: {}
      });
      
      // Confirmação e próxima seção
      setTimeout(() => {
        addBotMessage('Perfeito! Entendi como você quer que o agente atue.', false);
        setTimeout(() => goToNextSection(), 800);
      }, 300);
    } else {
      // Próxima sub-pergunta
      setObjectiveFollowUp(prev => ({
        ...prev,
        currentQuestionIndex: nextIndex,
        answers: newAnswers
      }));
      
      setTimeout(() => {
        addBotMessage(questions[nextIndex].question, true);
      }, 300);
    }
  }, [objectiveFollowUp, addBotMessage, goToNextSection]);

  // Adiciona confirmação e vai para próxima seção
  const addConfirmationAndProceed = useCallback((sectionKey, option = null) => {
    const confirmMessage = getConfirmationMessage(sectionKey, option);
    
    setIsTyping(true);
    
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'bot',
        content: confirmMessage
      }]);
      setIsTyping(false);
      
      // Se é objetivo e tem sub-perguntas
      if (sectionKey === 'objective' && option && OBJECTIVE_QUESTIONS[option.id]) {
        setTimeout(() => {
          startObjectiveFollowUp(option.id);
        }, 600);
      } else {
        setTimeout(() => {
          goToNextSection();
        }, 600);
      }
    }, 600);
  }, [goToNextSection, startObjectiveFollowUp]);

  // Handle option selection
  const handleOptionSelect = useCallback((option) => {
    addUserMessage(option.label);
    
    setResponses(prev => ({
      ...prev,
      [currentSectionKey]: {
        selected: option.id,
        custom: option.custom || null
      }
    }));
    
    setTimeout(() => {
      addConfirmationAndProceed(currentSectionKey, option);
    }, 300);
  }, [currentSectionKey, addUserMessage, addConfirmationAndProceed]);

  // Handle text/data submission
  const handleTextSubmit = useCallback((data) => {
    // Se estamos em sub-perguntas do objetivo
    if (objectiveFollowUp.active) {
      const displayText = typeof data === 'string' 
        ? (data.length > 80 ? `"${data.substring(0, 80)}..."` : `"${data}"`)
        : '';
      
      addUserMessage(displayText);
      
      setTimeout(() => {
        advanceObjectiveFollowUp(data);
      }, 300);
      return;
    }
    
    // Fluxo normal
    let displayText = '';
    let responseData = {};
    
    if (typeof data === 'string') {
      displayText = data.length > 100 ? `"${data.substring(0, 100)}..."` : `"${data}"`;
      responseData = { value: data };
    } else if (data.items) {
      displayText = `${data.items.length} pergunta(s) frequente(s) adicionada(s)`;
      responseData = data;
    } else if (data.selected) {
      displayText = `${data.selected.length} regra(s) selecionada(s)`;
      if (data.additional) {
        displayText += ` + regras personalizadas`;
      }
      responseData = data;
    } else {
      displayText = JSON.stringify(data);
      responseData = data;
    }
    
    addUserMessage(displayText);
    
    setResponses(prev => ({
      ...prev,
      [currentSectionKey]: responseData
    }));
    
    setTimeout(() => {
      addConfirmationAndProceed(currentSectionKey);
    }, 300);
  }, [currentSectionKey, objectiveFollowUp, addUserMessage, addConfirmationAndProceed, advanceObjectiveFollowUp]);

  // Handle skip
  const handleSkip = useCallback(() => {
    // Se estamos em sub-perguntas do objetivo
    if (objectiveFollowUp.active) {
      addUserMessage('Pulei esta pergunta');
      
      setTimeout(() => {
        advanceObjectiveFollowUp(null);
      }, 300);
      return;
    }
    
    // Fluxo normal
    addUserMessage('Pulei esta etapa');
    
    setResponses(prev => ({
      ...prev,
      [currentSectionKey]: { skipped: true }
    }));
    
    setTimeout(() => {
      setIsTyping(true);
      
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'bot',
          content: 'Sem problemas! Vamos continuar.'
        }]);
        setIsTyping(false);
        
        setTimeout(() => {
          goToNextSection();
        }, 600);
      }, 600);
    }, 300);
  }, [currentSectionKey, objectiveFollowUp, addUserMessage, goToNextSection, advanceObjectiveFollowUp]);

  // Handle audio transcription
  const handleTranscribe = useCallback(async (audioBlob) => {
    setIsProcessingAudio(true);
    
    try {
      const transcript = await transcribeAudio(audioBlob);
      
      if (transcript) {
        handleTextSubmit(transcript);
      } else {
        addBotMessage('Não consegui entender o áudio. Pode tentar novamente ou digitar?', true);
      }
    } catch (error) {
      console.error('Transcription error:', error);
      addBotMessage('Erro ao processar o áudio. Por favor, tente digitar.', true);
    } finally {
      setIsProcessingAudio(false);
    }
    
    return null;
  }, [handleTextSubmit, addBotMessage]);

  // ============================================
  // COMPLETION - Com Azure OpenAI
  // ============================================

  const handleComplete = useCallback(async () => {
  setIsCreating(true);
  setLoadingStage(0);
  setCurrentPhase(PHASES.COMPLETE);
  
  // Dados originais do onboarding
  const onboardingData = {
    business_category: selectedCategory?.id,
    sections: responses
  };

  console.log('=== ONBOARDING ORIGINAL ===');
  console.log(JSON.stringify(onboardingData, null, 2));
  console.log('===========================');

  // Simular progressão de loading stages
  const progressLoading = () => {
    return new Promise((resolve) => {
      let stage = 0;
      
      const advanceStage = () => {
        if (stage < LOADING_STAGE_DURATIONS.length - 1) {
          setTimeout(() => {
            stage++;
            setLoadingStage(stage);
            advanceStage();
          }, LOADING_STAGE_DURATIONS[stage]);
        } else {
          setTimeout(resolve, LOADING_STAGE_DURATIONS[stage]);
        }
      };
      
      advanceStage();
    });
  };

  try {
    // Iniciar progresso visual E chamada à API em paralelo
    const [_, enhanced] = await Promise.all([
      progressLoading(),
      enhanceOnboardingData(onboardingData)
    ]);

    // Salvar resultado aprimorado
    setEnhancedResult(enhanced);
    setAgentName(enhanced.agent_name || 'Meu Agente');

    console.log('========================================');
    console.log('🎉 DADOS APRIMORADOS');
    console.log('========================================');
    console.log(JSON.stringify(enhanced, null, 2));
    console.log('========================================');

    // =====================================================
    // 🆕 NOVA PARTE: CRIAR AGENTE NO BACKEND
    // =====================================================
    
    // Preparar payload para API
    const agentPayload = prepareOnboardingPayload(enhanced, selectedCategory?.id);
    
    console.log('📤 Enviando para backend:', agentPayload);
    
    // Criar agente via API
    const createResult = await createAgentFromOnboarding(agentPayload);
    
    console.log('========================================');
    console.log('✅ AGENTE CRIADO COM SUCESSO');
    console.log('========================================');
    console.log('Agent ID:', createResult.data?.agent?.id);
    console.log('Dados:', JSON.stringify(createResult, null, 2));
    console.log('========================================');

    // Salvar dados finais para uso posterior
    const finalData = {
      business_category: selectedCategory?.id,
      original_sections: responses,
      enhanced_sections: enhanced.sections,
      faq: enhanced.faq,
      agent_name: enhanced.agent_name,
      agent_id: createResult.data?.agent?.id,
      agent: createResult.data?.agent
    };

    // Opcional: salvar no localStorage para debug
    localStorage.setItem('lumensales_last_agent', JSON.stringify(finalData));

    // Clear onboarding cache on success
    localStorage.removeItem(ONBOARDING_CACHE_KEY);

  } catch (error) {
    console.error('❌ Erro no processo de conclusão:', error);
    
    // Mostrar erro para o usuário (você pode adicionar um estado de erro)
    // setError(error.message || 'Erro ao criar agente');
    
    // Fallback: usar dados originais
    const identityValue = responses.identity?.value || '';
    const extractedName = identityValue.match(/(?:somos? (?:a|o)|chamamos?|empresa)\s+([A-Za-zÀ-ú]+)/i)?.[1] 
      || selectedCategory?.label 
      || 'Assistente';
    setAgentName(extractedName);
  } finally {
    setIsCreating(false);
  }
}, [selectedCategory, responses]);

  // Handler para mudança de nome do agente
  const handleAgentNameChange = useCallback((newName) => {
    setAgentName(newName);
    
    // Atualizar no resultado aprimorado se existir
    if (enhancedResult) {
      setEnhancedResult(prev => ({
        ...prev,
        agent_name: newName
      }));
    }

    console.log('📝 Nome do agente atualizado:', newName);
  }, [enhancedResult]);

  const handleGoToInbox = () => {
  // Log final com nome atualizado
  if (enhancedResult) {
    console.log('========================================');
    console.log('🚀 NAVEGANDO PARA INBOX');
    console.log('========================================');
    console.log('Nome do Agente:', agentName);
    console.log('Categoria:', selectedCategory?.id);
    console.log('========================================');
  }
  
  // Navegar baseado no canal selecionado
  const defaultChannel = user?.default_channel;
  
  if (defaultChannel === 'wa_business') {
    navigate('/wa-business');
  } else {
    navigate('/inbox');
  }
};

  const handleConfigureMore = () => {
    navigate('/agents');
  };

  // ============================================
  // HELPERS
  // ============================================

  const getConfirmationMessage = (sectionKey, option = null) => {
    const messages = {
      identity: 'Perfeito! Entendi as informações sobre seu negócio.',
      tone_of_voice: option?.custom 
        ? 'Entendi o tom personalizado que você quer!' 
        : `Excelente! Tom ${option?.label?.toLowerCase() || 'definido'}.`,
      objective: `Ótimo! Objetivo: ${option?.label || 'definido'}. Vou fazer algumas perguntas para entender melhor.`,
      rules: 'Regras e limites configurados!',
      faq: 'FAQs adicionados com sucesso!',
      human_handoff: 'Entendi quando devo chamar você!',
      customization: 'Personalização extra registrada!'
    };
    
    return messages[sectionKey] || 'Entendido! Vamos continuar.';
  };

  // ============================================
  // STYLES
  // ============================================

  const styles = {
    container: {
      display: 'flex',
      minHeight: '100vh',
      maxHeight: '100vh',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      overflow: 'hidden',
    },
    splitLeft: {
      flex: 1,
      display: isMobile ? 'none' : 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '48px',
      backgroundColor: colors.bgPrimary,
      borderRight: `1px solid ${colors.borderColor}`,
      position: 'relative',
      overflow: 'hidden',
    },
    splitLeftBg: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 70% 80%, ${colors.accentPrimary}15 0%, transparent 50%)`,
      pointerEvents: 'none',
    },
    splitLeftContent: {
      position: 'relative',
      zIndex: 1,
      maxWidth: '420px',
    },
    splitRight: {
      flex: isMobile ? 1 : 1.2,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    fullScreen: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      maxHeight: '100vh',
      overflow: 'hidden',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '48px',
    },
    logoIcon: {
      width: '48px',
      height: '48px',
      backgroundColor: colors.accentPrimary,
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '24px',
      fontWeight: 700,
    },
    logoText: {
      fontSize: '24px',
      fontWeight: 700,
      color: colors.textPrimary,
    },
    title: {
      fontSize: isMobile ? '28px' : '38px',
      fontWeight: 700,
      lineHeight: 1.2,
      marginBottom: '20px',
      color: colors.textPrimary,
    },
    titleHighlight: {
      background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, #34d399 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    description: {
      fontSize: '17px',
      lineHeight: 1.7,
      color: colors.textSecondary,
    },
  };

  // ============================================
  // RENDER PHASES
  // ============================================

  const renderIntroPhase = () => (
    <div style={styles.container}>
      <div style={styles.splitRight}>
        <IntroCard 
          onStart={handleStart}
          userName={user?.name}
        />
      </div>
    </div>
  );

  const renderCategoryPhase = () => (
    <div style={styles.container}>
      <div style={styles.splitRight}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flex: 1,
          padding: '24px',
          overflow: 'auto',
        }}>
          <CategoryCards 
            categories={BUSINESS_CATEGORIES}
            onSelect={handleCategorySelect}
          />
        </div>
      </div>
    </div>
  );

  const renderChatPhase = () => {
    const currentConfig = getCurrentConfig();
    const headerSubtitle = objectiveFollowUp.active 
      ? `Pergunta ${objectiveFollowUp.currentQuestionIndex + 1} de ${objectiveFollowUp.questions.length}`
      : currentConfig?.title || 'Responda as perguntas para personalizar';

    return (
      <div style={styles.fullScreen}>
        <OnboardingChatUI
          messages={messages}
          isTyping={isTyping}
          inputReady={inputReady}
          sectionConfig={currentConfig}
          onOptionSelect={handleOptionSelect}
          onTextSubmit={handleTextSubmit}
          onSkip={handleSkip}
          onTranscribe={handleTranscribe}
          isProcessingAudio={isProcessingAudio}
          headerTitle="Criando seu agente"
          headerSubtitle={headerSubtitle}
        />
      </div>
    );
  };

  const renderCompletePhase = () => (
    <div style={styles.container}>
      <div style={styles.splitRight}>
        <CompleteCard 
          onGoToInbox={handleGoToInbox}
          onConfigureMore={handleConfigureMore}
          agentName={agentName}
          onAgentNameChange={handleAgentNameChange}
          isLoading={isCreating}
          loadingStage={loadingStage}
        />
      </div>
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  const renderPhase = () => {
    switch (currentPhase) {
      case PHASES.INTRO:
        return renderIntroPhase();
      case PHASES.CATEGORY:
        return renderCategoryPhase();
      case PHASES.CHAT:
        return renderChatPhase();
      case PHASES.COMPLETE:
        return renderCompletePhase();
      default:
        return renderIntroPhase();
    }
  };

  return (
    <>
      {renderPhase()}
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        html, body, #root {
          height: 100%;
          overflow: hidden;
        }
        body {
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default AgentOnboarding;